import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_GC_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import {
  IntegrationAlarm,
  IntegrationAlarmsResponse,
  IntegrationAuditResponse,
  IntegrationErrorsResponse,
  IntegrationDetailResponse,
  IntegrationMetaPatch,
  IntegrationPatchResult,
  IntegrationsListResponse,
  ProbeResult,
  SetEnabledResult,
  SpendResponse,
  TurnstileAnalytics,
} from '@/client/types/integrations';

const BASE = '/api/admin/v2/integrations';

/** Filters and paging for the list; every one is applied server-side. */
export interface IntegrationsParams {
  days?: number;
  page?: number;
  per_page?: number;
  q?: string;
  category?: string;
  status?: string;
  activity?: string;
  health?: string;
  unaudited?: boolean;
  erroring?: boolean;
  disabled?: boolean;
  month?: string;
}

const qs = (params: Record<string, unknown>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '' || value === false) return;
    search.set(key, value === true ? '1' : String(value));
  });
  const out = search.toString();
  return out ? `?${out}` : '';
};

export const integrationKeys = {
  all: ['integrations-v2'] as const,
  list: (params: IntegrationsParams) => [...integrationKeys.all, 'list', params] as const,
  errors: (params: object) => [...integrationKeys.all, 'errors', params] as const,
  detail: (name: string, params?: string) =>
    [...integrationKeys.all, 'detail', name, params ?? ''] as const,
  alarms: (status: string) => [...integrationKeys.all, 'alarms', status] as const,
  audit: (name: string) => [...integrationKeys.all, 'audit', name] as const,
  spend: (month?: string) => [...integrationKeys.all, 'spend', month ?? ''] as const,
  turnstile: (hours: number) => [...integrationKeys.all, 'turnstile', hours] as const,
};

/** Admin v2 wraps payloads in `{ status, data }`; accept either shape. */
const unwrap = <T,>(raw: unknown): T => {
  if (raw != null && typeof raw === 'object' && 'data' in raw && 'status' in raw) {
    return (raw as { data: T }).data;
  }
  return raw as T;
};

const monthQs = (month?: string) => (month ? `?month=${encodeURIComponent(month)}` : '');

const useListFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (params: IntegrationsParams): Promise<IntegrationsListResponse> => {
    const raw = await request<unknown>(`${BASE}${qs(params as Record<string, unknown>)}`);
    return unwrap<IntegrationsListResponse>(raw);
  }, 'Failed to load integrations');
};

export const useIntegrationsQuery = (params: IntegrationsParams = {}, enabled = true) => {
  const queryFn = useListFn();
  return useQuery({
    queryKey: integrationKeys.list(params),
    queryFn: () => queryFn(params),
    enabled,
    // Usage counters move continuously; the registry barely moves. 60s is a
    // compromise that keeps the page cheap on the admin app.
    staleTime: 1000 * 60,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

const useDetailFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(
    async (name: string, params: { month?: string; days?: number } = {}):
      Promise<IntegrationDetailResponse> => {
      const raw = await request<unknown>(`${BASE}/${encodeURIComponent(name)}${qs(params)}`);
      return unwrap<IntegrationDetailResponse>(raw);
    },
    'Failed to load integration',
  );
};

export const useIntegrationQuery = (
  name: string,
  params: { month?: string; days?: number } = {},
  enabled = true,
) => {
  const queryFn = useDetailFn();
  return useQuery({
    queryKey: integrationKeys.detail(name, JSON.stringify(params)),
    queryFn: () => queryFn(name, params),
    enabled: enabled && !!name,
    staleTime: 1000 * 60,
    gcTime: DEFAULT_GC_TIME,
  });
};

const useAlarmsFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (status: string): Promise<IntegrationAlarmsResponse> => {
    const raw = await request<unknown>(`${BASE}/alarms?status=${encodeURIComponent(status)}`);
    return unwrap<IntegrationAlarmsResponse>(raw);
  }, 'Failed to load integration alarms');
};

export const useIntegrationAlarmsQuery = (status = 'active', enabled = true) => {
  const queryFn = useAlarmsFn();
  return useQuery({
    queryKey: integrationKeys.alarms(status),
    queryFn: () => queryFn(status),
    enabled,
    staleTime: 1000 * 30,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

const useErrorsFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(
    async (params: { days?: number; name?: string; outcome?: string; limit?: number }):
      Promise<IntegrationErrorsResponse> => {
      const raw = await request<unknown>(`${BASE}/errors${qs(params)}`);
      return unwrap<IntegrationErrorsResponse>(raw);
    },
    'Failed to load vendor errors',
  );
};

export const useIntegrationErrorsQuery = (
  params: { days?: number; name?: string; outcome?: string; limit?: number },
  enabled = true,
) => {
  const queryFn = useErrorsFn();
  return useQuery({
    queryKey: integrationKeys.errors(params),
    queryFn: () => queryFn(params),
    enabled,
    staleTime: 1000 * 30,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

const useSpendFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (month?: string): Promise<SpendResponse> => {
    const raw = await request<unknown>(`${BASE}/spend${monthQs(month)}`);
    return unwrap<SpendResponse>(raw);
  }, 'Failed to load integration spend');
};

export const useIntegrationSpendQuery = (month?: string, enabled = true) => {
  const queryFn = useSpendFn();
  return useQuery({
    queryKey: integrationKeys.spend(month),
    queryFn: () => queryFn(month),
    enabled,
    staleTime: 1000 * 60 * 5,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

const useAuditFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (name: string): Promise<IntegrationAuditResponse> => {
    const raw = await request<unknown>(`${BASE}/${encodeURIComponent(name)}/audit`);
    return unwrap<IntegrationAuditResponse>(raw);
  }, 'Failed to load change history');
};

export const useIntegrationAuditQuery = (name: string, enabled = true) => {
  const queryFn = useAuditFn();
  return useQuery({
    queryKey: integrationKeys.audit(name),
    queryFn: () => queryFn(name),
    enabled: enabled && !!name,
    staleTime: 1000 * 60,
    gcTime: DEFAULT_GC_TIME,
  });
};

const usePatchFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(
    async ({
      name,
      changes,
    }: {
      name: string;
      changes: IntegrationMetaPatch;
    }): Promise<IntegrationPatchResult> => {
      const raw = await request<unknown>(
        `${BASE}/${encodeURIComponent(name)}`,
        'PATCH',
        changes,
      );
      return unwrap<IntegrationPatchResult>(raw);
    },
    'Failed to save integration config',
  );
};

/** Sends only the changed fields; a null value clears an override. */
export const usePatchIntegrationMutation = () => {
  const queryClient = useQueryClient();
  const patchFn = usePatchFn();
  return useMutation({
    mutationFn: (vars: { name: string; changes: IntegrationMetaPatch }) => patchFn(vars),
    onSuccess: (_res, vars) => {
      // An override can change computed severity, so the list rolls up differently too.
      queryClient.invalidateQueries({ queryKey: integrationKeys.all });
      queryClient.invalidateQueries({ queryKey: integrationKeys.audit(vars.name) });
    },
  });
};

const useProbeFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (name: string): Promise<ProbeResult> => {
    const raw = await request<unknown>(`${BASE}/${encodeURIComponent(name)}/probe`, 'POST', {});
    return unwrap<ProbeResult>(raw);
  }, 'The probe failed');
};

/** Asks the vendor right now whether the credential and quota are good. */
export const useProbeIntegrationMutation = () => {
  const queryClient = useQueryClient();
  const probeFn = useProbeFn();
  return useMutation({
    mutationFn: (name: string) => probeFn(name),
    // The probe persists what it found, so the row's observed block changes.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: integrationKeys.all }),
  });
};

const useSetEnabledFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(
    async ({ name, enabled }: { name: string; enabled: boolean | null }): Promise<SetEnabledResult> => {
      const raw = await request<unknown>(
        `${BASE}/${encodeURIComponent(name)}/enabled`,
        'PUT',
        { enabled },
      );
      return unwrap<SetEnabledResult>(raw);
    },
    'Failed to change the integration state',
  );
};

/** Disables a vendor in this environment, or clears the override with null. */
export const useSetEnabledMutation = () => {
  const queryClient = useQueryClient();
  const setFn = useSetEnabledFn();
  return useMutation({
    mutationFn: (vars: { name: string; enabled: boolean | null }) => setFn(vars),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: integrationKeys.all }),
  });
};

const useAckFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(
    async ({ id, note }: { id: string; note?: string }): Promise<IntegrationAlarm> => {
      const raw = await request<unknown>(`${BASE}/alarms/${encodeURIComponent(id)}/ack`, 'POST', {
        note: note ?? '',
      });
      return unwrap<IntegrationAlarm>(raw);
    },
    'Failed to acknowledge alarm',
  );
};

export const useAckAlarmMutation = () => {
  const queryClient = useQueryClient();
  const ackFn = useAckFn();
  return useMutation({
    mutationFn: (vars: { id: string; note?: string }) => ackFn(vars),
    onSuccess: () => {
      // The ack changes the alarm's status and the open-alarm count on the list.
      queryClient.invalidateQueries({ queryKey: integrationKeys.all });
    },
  });
};

export const useTurnstileAnalyticsQuery = (hours: number, enabled = true) => {
  const request = useClientStore((s) => s.request);
  const queryFn = withErrorHandling(async (h: number): Promise<TurnstileAnalytics> => {
    const raw = await request<unknown>(`${BASE}/turnstile/analytics?hours=${h}`);
    return unwrap<TurnstileAnalytics>(raw);
  }, 'Failed to load Turnstile analytics');
  return useQuery({
    queryKey: integrationKeys.turnstile(hours),
    queryFn: () => queryFn(hours),
    enabled,
    // Each read is a live Cloudflare query; five minutes is fresh enough.
    staleTime: 1000 * 60 * 5,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};
